import React, { useState, useEffect } from 'react';
import { Button, Form, Alert, Spinner } from '@/components/ui';
import ResponsiveModal from '../../../components/ResponsiveModal';
import { ServiceArea, QuantifiedPlan, areasService } from '../../../services/areasService';
import QuantifiedPlansTable from './QuantifiedPlansTable';
import QuantifiedPlanModal from './QuantifiedPlanModal';
import { confirmAction } from '@/components/ui';
import { describeActionError } from '../../../utils/loadError';

interface ServiceAreaModalProps {
  show: boolean;
  onHide: () => void;
  serviceArea: ServiceArea | null;
  onSave: (serviceAreaData: Partial<ServiceArea>) => Promise<void>;
}

const ServiceAreaModal: React.FC<ServiceAreaModalProps> = ({
  show,
  onHide,
  serviceArea,
  onSave
}) => {
  const [formData, setFormData] = useState({
    name: '',
    code: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [quantifiedPlans, setQuantifiedPlans] = useState<QuantifiedPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [deletePlanError, setDeletePlanError] = useState<string | null>(null);
  const [showQuantifiedPlanModal, setShowQuantifiedPlanModal] = useState(false);
  const [selectedQuantifiedPlan, setSelectedQuantifiedPlan] = useState<QuantifiedPlan | null>(null);

  useEffect(() => {
    const fetchQuantifiedPlans = async () => {
      setDeletePlanError(null);
      if (show && serviceArea?.id) {
        setLoadingPlans(true);
        setPlansError(null);
        try {
          const plans = await areasService.getQuantifiedPlans(serviceArea.id);
          setQuantifiedPlans(plans);
        } catch (err: unknown) {
          console.error('Error fetching quantified plans:', err);
          const msg =
            err && typeof err === 'object' && 'response' in err
              ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
              : null;
          setPlansError(
            msg ||
              'Could not load quantified plans. If the API returns 500, the database may be missing the latest migration (e.g. QuantifiedPlan.isActive).'
          );
          setQuantifiedPlans([]);
        } finally {
          setLoadingPlans(false);
        }
      } else {
        setQuantifiedPlans([]);
        setPlansError(null);
      }
    };

    fetchQuantifiedPlans();
  }, [show, serviceArea?.id]);

  useEffect(() => {
    if (serviceArea) {
      setFormData({
        name: serviceArea.name || '',
        code: serviceArea.code || ''
      });
    } else {
      setFormData({
        name: '',
        code: ''
      });
    }
    setError(null);
  }, [serviceArea, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await onSave({
        name: formData.name,
        code: formData.code
      });
      onHide();
    } catch (err) {
      console.error('Error saving service area:', err);
      setError('Failed to save service area. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const isFormValid = formData.name.trim().length > 0;

  const handleAddQuantifiedPlan = () => {
    setSelectedQuantifiedPlan(null);
    setShowQuantifiedPlanModal(true);
  };

  const handleEditQuantifiedPlan = (plan: QuantifiedPlan) => {
    setSelectedQuantifiedPlan(plan);
    setShowQuantifiedPlanModal(true);
  };

  const handleDeleteQuantifiedPlan = async (id: number) => {
    if (!(await confirmAction({ title: 'Delete Quantified Plan?', confirmLabel: 'Delete', destructive: true }))) {
      return;
    }

    setDeletePlanError(null);
    try {
      await areasService.deleteQuantifiedPlan(id);
      if (serviceArea?.id) {
        const plans = await areasService.getQuantifiedPlans(serviceArea.id);
        setQuantifiedPlans(plans);
      }
    } catch (err) {
      console.error('Error deleting quantified plan:', err);
      setDeletePlanError(describeActionError(err, "Couldn't delete the quantified plan. Try again."));
    }
  };

  const handleSaveQuantifiedPlan = async (planData: Partial<QuantifiedPlan>) => {
    try {
      if (selectedQuantifiedPlan) {
        await areasService.updateQuantifiedPlan(selectedQuantifiedPlan.id, planData);
      } else {
        await areasService.createQuantifiedPlan({
          ...planData,
          serviceAreaId: serviceArea!.id
        } as Omit<QuantifiedPlan, 'id' | 'serviceArea' | 'createdAt' | 'updatedAt'>);
      }

      if (serviceArea?.id) {
        const plans = await areasService.getQuantifiedPlans(serviceArea.id);
        setQuantifiedPlans(plans);
      }

      setShowQuantifiedPlanModal(false);
      setSelectedQuantifiedPlan(null);
    } catch (err) {
      throw err;
    }
  };

  const modalFooter = (
    <>
      <Button variant="secondary" onClick={onHide} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        disabled={!isFormValid || loading}
        onClick={handleSubmit}
      >
        {loading ? (
          <>
            <Spinner animation="border" size="sm" className="tw-mr-2" />
            Saving...
          </>
        ) : (
          <>
            <i className="bi bi-check-lg tw-mr-2"></i>
            {serviceArea ? 'Update' : 'Create'} Service Area
          </>
        )}
      </Button>
    </>
  );

  return (
    <>
      <ResponsiveModal
        isOpen={show}
        onClose={onHide}
        title={serviceArea ? 'Edit Service Area' : 'Create New Service Area'}
        footer={modalFooter}
      >
        {error && (
          <Alert variant="danger">{error}</Alert>
        )}

        <Form.Group className="tw-mb-3">
          <Form.Label>Name *</Form.Label>
          <Form.Control
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Enter service area name"
            required
            disabled={loading}
          />
        </Form.Group>

        <Form.Group className="tw-mb-3">
          <Form.Label>Code</Form.Label>
          <Form.Control
            type="text"
            value={formData.code}
            onChange={(e) => handleInputChange('code', e.target.value)}
            placeholder="Enter service area code (optional)"
            disabled={loading}
          />
          <Form.Text className="tw-text-content-muted">
            Optional identifier code for this service area
          </Form.Text>
        </Form.Group>

        {serviceArea && (
          <div className="tw-mt-4">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
              <h5 className="tw-mb-0">Quantified Plans</h5>
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddQuantifiedPlan}
                disabled={loading || loadingPlans}
              >
                <i className="bi bi-plus-lg tw-mr-2"></i>
                Add Quantified Plan
              </Button>
            </div>
            {plansError && (
              <Alert variant="warning">
                {plansError}
              </Alert>
            )}
            {deletePlanError && (
              <Alert variant="danger" role="alert" dismissible onClose={() => setDeletePlanError(null)} className="tw-mb-3">
                {deletePlanError}
              </Alert>
            )}
            <QuantifiedPlansTable
              quantifiedPlans={quantifiedPlans}
              onEdit={handleEditQuantifiedPlan}
              onDelete={handleDeleteQuantifiedPlan}
              loading={loadingPlans}
            />
          </div>
        )}
      </ResponsiveModal>

      {serviceArea && (
        <QuantifiedPlanModal
          show={showQuantifiedPlanModal}
          onHide={() => {
            setShowQuantifiedPlanModal(false);
            setSelectedQuantifiedPlan(null);
          }}
          quantifiedPlan={selectedQuantifiedPlan}
          serviceAreaId={serviceArea.id}
          onSave={handleSaveQuantifiedPlan}
        />
      )}
    </>
  );
};

export default ServiceAreaModal;
